/*
 * ============================================================================
 * Cart Recommendations — metadata-driven cross-sell engine
 * ============================================================================
 *
 * Replaces the old hardcoded cart-upsell logic with a catalog-driven engine
 * that recommends the ONE product most likely to complete a highchair setup.
 *
 * Two layers live in this file:
 *
 *   A. Pure engine — computeRecommendation(cartItems, catalog)
 *      No DOM access. Exposed on window.LittlePukuCartRecs AND via CommonJS
 *      (module.exports) so scripts/test-cart-recommendations.mjs can require it.
 *
 *   B. Widget — an IIFE that wires the engine into every [data-cart-recs]
 *      container (cart drawer + cart page), renders the recommendation card,
 *      and re-computes whenever the cart changes.
 *
 * Design notes:
 *   - "Family" = the accessory category (footrest / leg wrap / placemat /
 *     cushion / insert). We never recommend a product whose family is already
 *     represented in the cart, so a footrest in the cart never yields another
 *     footrest, and a bundle that contains a footrest blocks the footrest
 *     family via its base_products.
 *   - Variant coherence: if the cart carries an option value (e.g. "Bamboo"
 *     from a footrest), we preselect the matching variant of the recommended
 *     product (e.g. the Bamboo leg wraps) so the set looks intentional.
 *   - No hardcoded product handles in the ranking logic. The keyword lists
 *     below are ONLY a fallback for products whose `type` is blank.
 * ============================================================================
 */
(function (root, factory) {
  var api = factory();
  // Browser global
  if (typeof root !== 'undefined') {
    root.LittlePukuCartRecs = api;
  }
  // CommonJS (Node test harness)
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }
})(typeof window !== 'undefined' ? window : this, function () {
  'use strict';

  // ---------------------------------------------------------------------------
  // A. PURE ENGINE
  // ---------------------------------------------------------------------------

  // Canonical accessory families. Order matters: more specific keywords first
  // (e.g. an "Inflatable Insert" is an insert, not a cushion, even though its
  // title contains "cushion"). Fallback ONLY — real products carry a `type`.
  var FAMILY_RULES = [
    { family: 'footrest', keywords: ['footrest', 'foot rest', 'foot-rest'] },
    { family: 'leg-wrap', keywords: ['leg wrap', 'legwrap', 'leg-wrap'] },
    { family: 'insert', keywords: ['inflatable', 'insert'] },
    { family: 'cushion', keywords: ['cushion'] },
    { family: 'placemat', keywords: ['placemat', 'place mat'] }
  ];

  // Variant option values that carry no cross-sell signal.
  var GENERIC_OPTION_VALUES = {
    'default title': true,
    'default': true,
    'one size': true,
    'one-size': true,
    'onesize': true,
    'title': true,
    '': true
  };

  function normalize(str) {
    return String(str == null ? '' : str).trim().toLowerCase();
  }

  // Infer a family from an arbitrary text blob (type, or handle + title).
  function inferFamily(text) {
    var t = normalize(text);
    if (!t) return null;
    for (var i = 0; i < FAMILY_RULES.length; i++) {
      var rule = FAMILY_RULES[i];
      for (var k = 0; k < rule.keywords.length; k++) {
        if (t.indexOf(rule.keywords[k]) !== -1) return rule.family;
      }
    }
    return null;
  }

  // Family of a catalog product: prefer its `type`, fall back to handle+title.
  function familyOf(product) {
    if (!product) return null;
    var byType = inferFamily(product.type);
    if (byType) return byType;
    return inferFamily((product.handle || '') + ' ' + (product.title || ''));
  }

  function isGiftCard(product) {
    if (!product) return false;
    if (normalize(product.type).indexOf('gift card') !== -1) return true;
    if (normalize(product.handle).indexOf('gift-card') !== -1) return true;
    return false;
  }

  // Build a { handle -> product } index for O(1) lookups.
  function indexByHandle(products) {
    var map = {};
    for (var i = 0; i < products.length; i++) {
      var p = products[i];
      if (p && p.handle) map[p.handle] = p;
    }
    return map;
  }

  // Build a { variantId(string) -> { product, variant } } index for O(1)
  // lookups. Used by cartItemOptionValues() to resolve a cart item's real
  // option values directly from OUR OWN trusted catalog data (every variant
  // in the catalog carries its `id`), sidestepping whatever option-value
  // fields the cart item JSON itself does or doesn't happen to carry.
  function indexVariantsById(products) {
    var map = {};
    for (var i = 0; i < products.length; i++) {
      var p = products[i];
      if (!p || !Array.isArray(p.variants)) continue;
      for (var j = 0; j < p.variants.length; j++) {
        var v = p.variants[j];
        if (v && v.id != null) map[String(v.id)] = { product: p, variant: v };
      }
    }
    return map;
  }

  // Extract normalized, non-generic option values for a single cart item.
  //
  // BUG FIX (2026-07): Shopify's Liquid `{{ cart | json }}` serialization
  // (used for the drawer's initial paint via #initial-cart) does NOT include
  // `options_with_values` or `variant_options` on cart items — those are
  // AJAX-only fields specific to the /cart.js REST response schema, absent
  // from the Liquid CartItemDrop's JSON output. Every cart item shape DOES
  // reliably carry a variant id though (`variant_id` in the AJAX shape,
  // `id` in some Liquid/AJAX line-item shapes), so that's the robust primary
  // signal: resolve it against `variantIndex` (built from our own catalog,
  // which always has trustworthy `variant.options`) and use THAT variant's
  // options — this works regardless of which cart JSON shape produced the
  // item, and requires no per-field guessing.
  //
  // FALLBACK: if the variant isn't found in the catalog (e.g. a cart item
  // for a product outside the recommendation catalog — shouldn't normally
  // happen, but defensive), fall back to the original options_with_values /
  // variant_options extraction, which is still correct for AJAX-sourced
  // carts that DO carry those fields (e.g. a cart:updated payload built from
  // a fresh /cart.js fetch).
  function cartItemOptionValues(item, variantIndex) {
    if (item && variantIndex) {
      var vid = item.variant_id != null ? item.variant_id : item.id;
      if (vid != null) {
        var hit = variantIndex[String(vid)];
        if (hit && hit.variant && Array.isArray(hit.variant.options)) {
          return hit.variant.options
            .map(function (o) { return normalize(o); })
            .filter(function (v) { return !GENERIC_OPTION_VALUES.hasOwnProperty(v); });
        }
      }
    }

    var out = [];
    if (item && Array.isArray(item.options_with_values)) {
      for (var i = 0; i < item.options_with_values.length; i++) {
        var ov = item.options_with_values[i];
        if (ov && ov.value != null) out.push(normalize(ov.value));
      }
    } else if (item && Array.isArray(item.variant_options)) {
      for (var j = 0; j < item.variant_options.length; j++) {
        out.push(normalize(item.variant_options[j]));
      }
    }
    return out.filter(function (v) {
      return !GENERIC_OPTION_VALUES.hasOwnProperty(v);
    });
  }

  // Count how many normalized option values of a catalog variant intersect the
  // cart's option-value set.
  function variantOverlap(variant, cartOptionSet) {
    if (!variant || !Array.isArray(variant.options)) return 0;
    var count = 0;
    var seen = {};
    for (var i = 0; i < variant.options.length; i++) {
      var v = normalize(variant.options[i]);
      if (GENERIC_OPTION_VALUES.hasOwnProperty(v)) continue;
      if (seen[v]) continue;
      seen[v] = true;
      if (cartOptionSet[v]) count++;
    }
    return count;
  }

  // Pick the best available variant of a candidate product.
  // Best = most option-value overlap with the cart; ties / no overlap fall back
  // to the first available variant. Returns { variant, overlap } or null.
  function bestVariant(product, cartOptionSet) {
    if (!product || !Array.isArray(product.variants)) return null;
    var available = product.variants.filter(function (v) {
      return v && v.available;
    });
    if (!available.length) return null;

    var best = null;
    var bestOverlap = -1;
    for (var i = 0; i < available.length; i++) {
      var overlap = variantOverlap(available[i], cartOptionSet);
      if (overlap > bestOverlap) {
        bestOverlap = overlap;
        best = available[i];
      }
    }
    return { variant: best, overlap: Math.max(0, bestOverlap) };
  }

  // Shared by computeRecommendation() and the debug-only rankCandidates()
  // below — builds the full ranked candidate list (coherent first, then
  // cheapest). Returns [] if nothing qualifies. No behavior change from the
  // original inline version; extracted only so diagnostics can see the full
  // ranking, not just the winner.
  function buildRankedCandidates(cartItems, catalog) {
    if (!cartItems || !cartItems.length) return [];
    if (!catalog || !Array.isArray(catalog.products) || !catalog.products.length) {
      return [];
    }

    var products = catalog.products;
    var byHandle = indexByHandle(products);
    var variantIndex = indexVariantsById(products);

    // 1. Cart family fingerprint --------------------------------------------
    var cartFamilies = {};
    var cartHandles = {};
    var cartOptionSet = {};

    for (var i = 0; i < cartItems.length; i++) {
      var item = cartItems[i];
      if (!item) continue;
      var handle = normalize(item.handle);

      // Some cart sources (e.g. this theme's /cart?view=compare, used to
      // enrich the drawer with live stock data) return a stripped item shape
      // with only a variant id — no handle, no product_type. Resolve the
      // product via the same variant-id catalog lookup used for option
      // values below, so family exclusion still works (a cart item must
      // never cause its own product/family to be recommended back to it).
      var resolvedEntry = null;
      if (!handle && variantIndex) {
        var vidForHandle = item.variant_id != null ? item.variant_id : item.id;
        var handleHit = vidForHandle != null ? variantIndex[String(vidForHandle)] : null;
        if (handleHit && handleHit.product) {
          resolvedEntry = handleHit.product;
          handle = normalize(handleHit.product.handle);
        }
      }
      if (handle) cartHandles[handle] = true;

      // Option values for coherence scoring.
      var vals = cartItemOptionValues(item, variantIndex);
      for (var v = 0; v < vals.length; v++) cartOptionSet[vals[v]] = true;

      var entry = resolvedEntry || (handle ? byHandle[handle] : null);

      if (entry && entry.isBundle && Array.isArray(entry.baseHandles) && entry.baseHandles.length) {
        // Bundle: expand to the families of its base products.
        for (var b = 0; b < entry.baseHandles.length; b++) {
          var baseHandle = normalize(entry.baseHandles[b]);
          var baseProduct = byHandle[baseHandle];
          var baseFamily = baseProduct ? familyOf(baseProduct) : inferFamily(baseHandle);
          if (baseFamily) cartFamilies[baseFamily] = true;
        }
      } else if (entry) {
        var fam = familyOf(entry);
        if (fam) cartFamilies[fam] = true;
      } else {
        // Not in catalog — infer from the cart item's own metadata.
        var inferred = inferFamily(item.product_type) ||
          inferFamily((item.handle || '') + ' ' + (item.product_title || ''));
        if (inferred) cartFamilies[inferred] = true;
      }
    }

    // 2. Build candidate list ------------------------------------------------
    var candidates = [];
    for (var p = 0; p < products.length; p++) {
      var prod = products[p];
      if (!prod || !prod.available) continue;
      if (prod.isBundle) continue;
      if (isGiftCard(prod)) continue;
      if (cartHandles[normalize(prod.handle)]) continue;

      var family = familyOf(prod);
      if (!family) continue;
      if (cartFamilies[family]) continue;

      var bv = bestVariant(prod, cartOptionSet);
      if (!bv) continue; // no available variant

      candidates.push({
        product: prod,
        variant: bv.variant,
        coherent: bv.overlap > 0,
        priceMin: typeof prod.priceMin === 'number' ? prod.priceMin : Infinity
      });
    }

    if (!candidates.length) return [];

    // 3. Rank: coherent first, then cheapest --------------------------------
    candidates.sort(function (a, b) {
      if (a.coherent !== b.coherent) return a.coherent ? -1 : 1;
      return a.priceMin - b.priceMin;
    });

    return candidates;
  }

  /**
   * Compute the single best cross-sell recommendation for a cart.
   *
   * @param {Array}  cartItems  Shopify /cart.js-style items.
   * @param {Object} catalog    { products: [...] } (see file header for shape).
   * @returns {{product, variant, coherent}|null}
   */
  function computeRecommendation(cartItems, catalog) {
    var candidates = buildRankedCandidates(cartItems, catalog);
    if (!candidates.length) return null;
    var winner = candidates[0];
    return {
      product: winner.product,
      variant: winner.variant,
      coherent: winner.coherent
    };
  }

  // Debug-only: full ranked list (top N) for the on-screen diagnostic panel.
  // Never called outside DEBUG.on — zero cost in production.
  function rankCandidates(cartItems, catalog, limit) {
    var candidates = buildRankedCandidates(cartItems, catalog);
    var n = typeof limit === 'number' ? limit : 5;
    return candidates.slice(0, n).map(function (c) {
      return {
        handle: c.product && c.product.handle,
        variant: c.variant && c.variant.id,
        variantTitle: c.variant && c.variant.title,
        coherent: c.coherent,
        priceMin: c.priceMin,
        available: c.product && c.product.available
      };
    });
  }

  var engine = {
    computeRecommendation: computeRecommendation,
    // Exposed for testing / debugging.
    _familyOf: familyOf,
    _inferFamily: inferFamily,
    _rankCandidates: rankCandidates
  };

  // ---------------------------------------------------------------------------
  // B. WIDGET LAYER (browser only)
  // ---------------------------------------------------------------------------
  if (typeof document !== 'undefined' && typeof window !== 'undefined') {
    (function initWidget() {
      var DEBOUNCE_DELAY = 100;

      // -----------------------------------------------------------------------
      // Opt-in on-screen debug readout (per CLAUDE.md debugging rule).
      // OFF by default. Enable via ?cartrecs_debug=1 (query OR hash) or
      // localStorage 'cartrecs_debug' === '1'. When off, DEBUG.on is false and
      // DEBUG.log is a no-op — zero DOM, zero measurable overhead.
      // -----------------------------------------------------------------------
      var DEBUG = (function () {
        var enabled = false;
        try {
          var probe = String(window.location.search || '') + ' ' + String(window.location.hash || '');
          if (probe.indexOf('cartrecs_debug=1') !== -1) enabled = true;
          if (!enabled && window.localStorage && window.localStorage.getItem('cartrecs_debug') === '1') {
            enabled = true;
          }
        } catch (e) {
          enabled = false;
        }

        if (!enabled) {
          return { on: false, log: function () {} };
        }

        var MAX_ENTRIES = 30;
        var entries = [];
        var panel = null;
        var bodyEl = null;

        function ensurePanel() {
          if (panel) return;
          panel = document.createElement('div');
          panel.id = 'cartrecs-debug-panel';
          panel.style.cssText = [
            'position:fixed', 'bottom:8px', 'right:8px', 'z-index:2147483647',
            'width:min(360px,92vw)', 'max-height:52vh', 'overflow:auto',
            'background:rgba(18,18,18,0.95)', 'color:#31d431',
            'font:11px/1.35 ui-monospace,SFMono-Regular,Menlo,Consolas,monospace',
            'padding:8px 10px', 'border-radius:6px', 'border:1px solid #31d431',
            'white-space:pre-wrap', 'word-break:break-word',
            '-webkit-user-select:text', 'user-select:text',
            'box-shadow:0 6px 20px rgba(0,0,0,0.55)', 'pointer-events:auto'
          ].join(';');
          var head = document.createElement('div');
          head.textContent = 'cart-recs debug — select to copy · tap ✕ to hide';
          head.style.cssText = 'color:#9dff9d;font-weight:700;margin-bottom:6px;cursor:pointer;';
          head.addEventListener('click', function () { panel.style.display = 'none'; });
          bodyEl = document.createElement('div');
          panel.appendChild(head);
          panel.appendChild(bodyEl);
          (document.body || document.documentElement).appendChild(panel);
        }

        function log(label, data) {
          var ts = '';
          try { ts = new Date().toISOString().substr(11, 12); } catch (e) { ts = '' + Date.now(); }
          var line = ts + '  ' + label;
          if (data !== undefined) {
            var val;
            try { val = typeof data === 'string' ? data : JSON.stringify(data); }
            catch (e) { val = String(data); }
            line += ': ' + val;
          }
          entries.push(line);
          if (entries.length > MAX_ENTRIES) entries.shift();
          try { ensurePanel(); if (bodyEl) bodyEl.textContent = entries.join('\n'); } catch (e) {}
          if (window.console && console.log) console.log('[cart-recs]', line);
        }

        return { on: true, log: log };
      })();

      // Inspect window.wetheme.updateCartDrawer and classify the implementation
      // (no-op stub vs the real delegating renderer). Cheap; drives the fallback
      // decision below AND feeds the debug panel.
      function wethemeImplInfo() {
        var info = { hasWetheme: !!window.wetheme, type: 'undefined', impl: 'none' };
        try {
          if (window.wetheme) {
            var fn = window.wetheme.updateCartDrawer;
            info.type = typeof fn;
            if (typeof fn === 'function') {
              var src = Function.prototype.toString.call(fn).replace(/\s+/g, ' ').trim();
              info.len = src.length;
              info.snippet = src.slice(0, 90);
              if (src.indexOf('cartDrawer') !== -1) {
                info.impl = 'real(delegating)';
              } else if (/=>\s*\{\s*\}$/.test(src) || src.length < 30) {
                info.impl = 'stub(no-op)';
              } else {
                info.impl = 'other';
              }
            }
          }
        } catch (e) {
          info.error = String(e);
        }
        return info;
      }

      function parseJSON(text) {
        try {
          return JSON.parse(text);
        } catch (e) {
          return null;
        }
      }

      // --- money formatting --------------------------------------------------
      // Minimal Shopify-style money formatter, used only when the theme's own
      // formatter is unavailable.
      function fallbackFormatMoney(cents, format) {
        var value = (Number(cents) || 0) / 100;
        if (!format) return '$' + value.toFixed(2);
        function withCommas(number, precision, thousands, decimal) {
          var str = (Number(number) || 0).toFixed(precision);
          var parts = str.split('.');
          var intPart = parts[0].replace(/(\d)(?=(\d{3})+$)/g, '$1' + (thousands || ','));
          return parts[1] ? intPart + (decimal || '.') + parts[1] : intPart;
        }
        return format.replace(/\{\{\s*(\w+)\s*\}\}/g, function (match, token) {
          switch (token) {
            case 'amount':
              return withCommas(value, 2, ',', '.');
            case 'amount_no_decimals':
              return withCommas(value, 0, ',', '.');
            case 'amount_with_comma_separator':
              return withCommas(value, 2, '.', ',');
            case 'amount_no_decimals_with_comma_separator':
              return withCommas(value, 0, '.', ',');
            case 'amount_with_space_separator':
              return withCommas(value, 2, ' ', ',');
            default:
              return withCommas(value, 2, ',', '.');
          }
        });
      }

      // The amount and nothing else: no markup, no screen-reader label. Returns
      // null when the presentment currency matches the shop's, so callers fall
      // back to the shop format rather than this deciding it for them.
      function intlAmount(cents) {
        var root = document.querySelector('[data-cart-recs]');
        var pc = root && root.getAttribute('data-presentment-currency');
        var sc = root && root.getAttribute('data-shop-currency');
        if (!pc || !sc || pc === sc || typeof Intl === 'undefined') return null;
        // Locale pinned to 'en' to match the theme's leading-symbol convention;
        // de-DE would render EUR as "45,00 €" beside server amounts written "€45.00".
        try {
          return new Intl.NumberFormat('en', {
            style: 'currency', currency: pc, currencyDisplay: 'narrowSymbol',
          }).format((Number(cents) || 0) / 100);
        } catch (e) {
          try {
            return new Intl.NumberFormat('en', { style: 'currency', currency: pc })
              .format((Number(cents) || 0) / 100);
          } catch (e2) { return null; }
        }
      }

      function formatMoney(cents, moneyFormat) {
      // Presentment currency first. window.theme is NEVER defined in this theme —
      // the theme object is exposed as window.wetheme (theme.js:39534) — so the
      // old `window.theme && window.theme.formatMoney` check could not match and
      // every non-AU amount fell through to the hardcoded '$'.
        var intl = intlAmount(cents);
        if (intl !== null) return intl;
        if (window.wetheme && typeof window.wetheme.formatMoney === 'function') {
          try {
            return window.wetheme.formatMoney(cents, moneyFormat);
          } catch (e) {
            /* fall through */
          }
        }
        return fallbackFormatMoney(cents, moneyFormat);
      }

      function sessionGet(key) {
        try {
          return window.sessionStorage.getItem(key);
        } catch (e) {
          return null;
        }
      }
      function sessionSet(key, val) {
        try {
          window.sessionStorage.setItem(key, val);
        } catch (e) {
          /* private browsing */
        }
      }

      function dismissKey(handle) {
        return 'cart_recs_dismissed_' + handle;
      }

      // --- per-container controller -----------------------------------------
      function setupContainer(container) {
        if (container.getAttribute('data-recs-initialized') === 'true') return;
        container.setAttribute('data-recs-initialized', 'true');

        var catalog = null;
        var strings = {
          heading: container.getAttribute('data-str-heading') || 'Goes well with your cart',
          add: container.getAttribute('data-str-add') || 'Add',
          adding: container.getAttribute('data-str-adding') || 'Adding…',
          added: container.getAttribute('data-str-added') || 'Added',
          dismiss: container.getAttribute('data-str-dismiss') || 'No thanks',
          error: container.getAttribute('data-str-error') || 'Try again'
        };
        var moneyFormat = container.getAttribute('data-money-format') || '';

        // Parse embedded catalog (wrapped so malformed JSON can't break the drawer).
        try {
          var catalogScript = container.querySelector('script[type="application/json"]');
          if (catalogScript) {
            catalog = parseJSON(catalogScript.textContent);
          }
        } catch (e) {
          catalog = null;
        }
        if (!catalog || !Array.isArray(catalog.products)) {
          if (DEBUG.on) DEBUG.log('init FAILED — no catalog on container');
          container.style.display = 'none';
          return;
        }

        if (DEBUG.on) {
          // Build marker so a debug capture can prove which JS asset content
          // is actually executing — settles "did my fix actually deploy or
          // is a CDN/edge cache serving the old file?" without guessing.
          DEBUG.log('BUILD', 'variant-id-coherence-fix-2026-07-03c');
          DEBUG.log('init OK — catalog products', catalog.products.length);
        }

        function hide() {
          container.style.display = 'none';
        }

        // --- money markup helper (matches theme.js drawer output) -------------
        // window.wetheme.formatMoney already returns a <span class="money">…</span>
        // wrapper; the fallback returns a bare string, so wrap that case.
        // The amount with no wrapper and no screen-reader label, for places where
        // the surrounding text already supplies the context (an "Add - $12.95"
        // button).
        //
        // Built from intlAmount/fallbackFormatMoney rather than formatMoney,
        // because wetheme.formatMoney embeds an sr-only "Cart Subtotal" INSIDE the
        // markup. Stripping tags leaves that text behind, so the earlier version
        // of this produced a visible "Add - Cart Subtotal$12.95" — worse than the
        // nesting it was meant to fix.
        function plainMoney(cents) {
          var intl = intlAmount(cents);
          if (intl !== null) return intl;
          return fallbackFormatMoney(cents, moneyFormat);
        }

        function moneyHtml(cents) {
          var m = formatMoney(cents, moneyFormat);
          if (String(m).indexOf('<span') !== -1) return m;
          return '<span class="money">' + escapeHtml(m) + '</span>';
        }

        // --- guarantee header count + totals ---------------------------------
        // Runs independently of the theme's drawer renderer so these values are
        // always correct even if window.wetheme.updateCartDrawer is missing,
        // a no-op stub, or throws. Mirrors the selectors theme.js updates.
        function updateDrawerTotals(cart) {
          var count = cart && typeof cart.item_count === 'number' ? cart.item_count : null;
          if (count !== null) {
            var parenCounts = document.querySelectorAll('[data-cart-count]');
            for (var i = 0; i < parenCounts.length; i++) {
              parenCounts[i].textContent = '(' + count + ')';
            }
            var plainCounts = document.querySelectorAll('.cart-item-count, .cart-item-count-header--quantity');
            for (var j = 0; j < plainCounts.length; j++) {
              plainCounts[j].textContent = count;
            }
          }
          if (cart && typeof cart.total_price === 'number') {
            var totals = document.querySelectorAll('.cart-total-price, .cart-item-count-header--total');
            for (var t = 0; t < totals.length; t++) {
              totals[t].innerHTML = moneyHtml(cart.total_price);
            }
            var origs = document.querySelectorAll('.cart-item-original-total-price');
            for (var o = 0; o < origs.length; o++) {
              if (typeof cart.original_total_price === 'number' && cart.original_total_price > cart.total_price) {
                origs[o].innerHTML = moneyHtml(cart.original_total_price);
                origs[o].style.display = '';
              } else {
                origs[o].style.display = 'none';
              }
            }
          }
        }

        // --- fallback item-list render ---------------------------------------
        // Renders any cart line not already present in the drawer, cloning the
        // theme's own #cart-item-template and keying each row by item.key
        // ("variantId:hash"). It intentionally does NOT fetch /cart?view=compare
        // (stock data) — that stays the theme's job. Because rows are keyed
        // exactly like theme.js expects, a subsequent (real) updateCartDrawer
        // run matches these rows by variant_id and updates them in place rather
        // than appending duplicates. Safe to run alongside the theme renderer.
        function renderDrawerItemsFallback(cart) {
          var list = document.querySelector('ul.cart-items');
          var tplEl = document.getElementById('cart-item-template');
          if (!list || !tplEl) return false;
          var tpl = tplEl.innerHTML;
          if (!tpl) return false;

          var present = {};
          var rows = list.querySelectorAll('.cart-drawer-v2__item');
          for (var r = 0; r < rows.length; r++) {
            var key = rows[r].getAttribute('data-line-item-key') || '';
            var vid = key.split(':')[0];
            if (vid) present[vid] = true;
          }

          var items = cart && Array.isArray(cart.items) ? cart.items : [];
          var added = 0;
          for (var i = 0; i < items.length; i++) {
            var it = items[i];
            var variantId = String(it.variant_id);
            if (present[variantId]) continue;

            var wrap = document.createElement('div');
            wrap.innerHTML = String(tpl).trim();
            var node = wrap.querySelector('.cart-drawer-v2__item') || wrap.firstElementChild;
            if (!node) continue;

            node.setAttribute('data-line-item-key', it.key || (variantId + ':'));

            var img = node.querySelector('.cart-item-image');
            if (img) {
              if (it.image) { img.setAttribute('src', it.image); img.setAttribute('alt', it.title || ''); }
              else { img.style.display = 'none'; }
            }
            var links = node.querySelectorAll('.cart-item-link');
            for (var L = 0; L < links.length; L++) links[L].setAttribute('href', it.url || '#');
            var titleEl = node.querySelector('.cart-item-product-title');
            if (titleEl) titleEl.textContent = it.product_title || it.title || '';
            var variantEl = node.querySelector('.cart-item-variant-title');
            if (variantEl && it.variant_title) { variantEl.classList.remove('hide'); variantEl.textContent = it.variant_title; }
            var priceEl = node.querySelector('.cart-item-price');
            if (priceEl) priceEl.innerHTML = moneyHtml(it.final_line_price);
            var qtyEl = node.querySelector('.cart-item-quantity');
            if (qtyEl) { qtyEl.value = it.quantity; qtyEl.setAttribute('name', 'updates[]'); }

            list.appendChild(node);
            present[variantId] = true;
            added++;
          }
          return added > 0;
        }

        // Reveal the (possibly empty) drawer body / hide the empty-cart panel,
        // matching theme.js so the newly-added row isn't hidden behind the
        // empty state on a previously-empty cart.
        function ensureDrawerBodyVisible(cart) {
          if (!cart || !Array.isArray(cart.items) || !cart.items.length) return;
          var empty = document.querySelector('.cart-empty-box');
          var bottom = document.querySelector('.ajax-cart--bottom-wrapper');
          if (empty) empty.classList.add('hide');
          if (bottom) bottom.classList.remove('hide');
        }

        // Open / keep the cart drawer open, mirroring the theme's own cross-sell
        // add flow (component-cross-sells.js dispatches cart:open after adding).
        function openCartDrawer() {
          try {
            var wrapper = document.getElementById('cartSlideoutWrapper');
            if (!wrapper) return;
            if (window.jQuery) {
              window.jQuery(wrapper).trigger('cart:open');
            } else {
              wrapper.dispatchEvent(new CustomEvent('cart:open'));
            }
          } catch (e) { /* drawer may not be present on this template */ }
        }

        // Single coordinated post-add drawer refresh. Every step is isolated so
        // one failing step can never block the others (the original bug: a
        // throw in updateCartDrawer skipped the count update AND the recompute).
        function refreshDrawerAfterAdd(cart) {
          // 1. Notify listeners (shipping progress bar + savings/bundle widgets
          //    read this). MUST stay.
          try {
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
            if (DEBUG.on) DEBUG.log('dispatched cart:updated', { item_count: cart.item_count });
          } catch (e) {
            if (DEBUG.on) DEBUG.log('ERROR dispatch cart:updated', String(e));
          }

          // 2. Guarantee header count + totals regardless of the drawer renderer.
          try {
            ensureDrawerBodyVisible(cart);
            updateDrawerTotals(cart);
          } catch (e) {
            if (DEBUG.on) DEBUG.log('ERROR updateDrawerTotals', String(e));
          }

          // 3. Proven full re-render via the theme's own drawer. It fetches
          //    /cart?view=compare internally, so we do NOT duplicate that.
          var impl = wethemeImplInfo();
          if (DEBUG.on) DEBUG.log('wetheme', impl);
          try {
            if (window.wetheme && typeof window.wetheme.updateCartDrawer === 'function') {
              window.wetheme.updateCartDrawer(cart);
              if (DEBUG.on) DEBUG.log('called wetheme.updateCartDrawer', { impl: impl.impl });
            } else if (DEBUG.on) {
              DEBUG.log('wetheme.updateCartDrawer unavailable');
            }
          } catch (e) {
            if (DEBUG.on) DEBUG.log('ERROR wetheme.updateCartDrawer', String(e));
          }

          // 4. Instant, duplicate-safe fallback render. Guarantees the new line
          //    appears even if step 3 was a no-op stub or threw. When step 3 is
          //    the real renderer, it reconciles these rows by variant_id (no
          //    duplicates) and enriches them with stock data.
          try {
            var applied = renderDrawerItemsFallback(cart);
            if (DEBUG.on) DEBUG.log('fallback item render', { applied: applied });
          } catch (e) {
            if (DEBUG.on) DEBUG.log('ERROR fallback render', String(e));
          }

          // 5. Keep the drawer open (mirror cross-sell add flow).
          openCartDrawer();
        }

        function escapeHtml(str) {
          return String(str == null ? '' : str)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
        }

        function render(result) {
          if (!result || !result.product || !result.variant) {
            hide();
            return;
          }
          var product = result.product;
          var variant = result.variant;

          // Respect per-product session dismissal.
          if (sessionGet(dismissKey(product.handle)) === 'true') {
            hide();
            return;
          }

          var imageUrl = variant.image || product.image || '';
          var variantLabel = '';
          if (result.coherent && Array.isArray(variant.options)) {
            var meaningful = variant.options.filter(function (o) {
              return o && GENERIC_OPTION_VALUES[normalize(o)] !== true;
            });
            if (meaningful.length) variantLabel = meaningful.join(' / ');
          }

          var html = '' +
            '<div class="cart-upsell" data-cart-recs-card>' +
            '<button type="button" class="cart-upsell__close" aria-label="' + escapeHtml(strings.dismiss) + '" data-recs-dismiss>' +
            '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">' +
            '<line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>' +
            '</button>' +
            '<p class="cart-upsell__heading">' + escapeHtml(strings.heading) + '</p>' +
            '<div class="cart-upsell__product">' +
            '<a href="' + escapeHtml(product.url) + '" class="cart-upsell__image-link" tabindex="-1">' +
            (imageUrl
              ? '<img src="' + escapeHtml(imageUrl) + '" alt="' + escapeHtml(product.title) + '" class="cart-upsell__image" width="60" height="60" loading="lazy">'
              : '') +
            '</a>' +
            '<div class="cart-upsell__details">' +
            // .cart-upsell__text is a purely ADDITIVE wrapper (a div, so the
            // <p> variant inside stays valid HTML): on mobile it becomes the
            // 2-line -webkit-line-clamp container so title + variant flow
            // inline ("Title — Variant"), mirroring the cart line-item
            // treatment. Desktop is unchanged — the div is unstyled there and
            // its children keep their existing block styling. All existing
            // .cart-upsell__* classes are preserved.
            '<div class="cart-upsell__text">' +
            '<a href="' + escapeHtml(product.url) + '" class="cart-upsell__title">' + escapeHtml(product.title) + '</a>' +
            (variantLabel ? ' <p class="cart-upsell__variant">' + escapeHtml(variantLabel) + '</p>' : '') +
            '</div>' +
            '<button type="button" class="cart-upsell__add-btn" data-recs-add data-variant-id="' + escapeHtml(String(variant.id)) + '">' +
            // plainMoney, not moneyHtml: wetheme.formatMoney embeds an sr-only
            // "Cart Subtotal" label, which is right in a cart total and wrong in an
            // upsell button — the accessible name became "Add - Cart Subtotal
            // $12.95". This needs the amount and nothing else.
            escapeHtml(strings.add) + ' - <span class="money">' +
            escapeHtml(plainMoney(variant.price)) + '</span>' +
            '</button>' +
            '</div></div></div>';

          container.innerHTML = html;
          container.style.display = '';
          wireCard(product);
        }

        function wireCard(product) {
          var dismissBtn = container.querySelector('[data-recs-dismiss]');
          if (dismissBtn) {
            dismissBtn.addEventListener('click', function () {
              sessionSet(dismissKey(product.handle), 'true');
              hide();
            });
          }

          var addBtn = container.querySelector('[data-recs-add]');
          if (addBtn) {
            addBtn.addEventListener('click', function () {
              addToCart(addBtn);
            });
          }
        }

        function addToCart(button) {
          var variantId = button.getAttribute('data-variant-id');
          var originalHtml = button.innerHTML;
          var addSucceeded = false;
          button.innerHTML = escapeHtml(strings.adding);
          button.disabled = true;
          if (DEBUG.on) DEBUG.log('add clicked — variant id', variantId);

          // Variant id MUST be a string to avoid precision loss on large ids.
          fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
          })
            .then(function (response) {
              if (DEBUG.on) DEBUG.log('/cart/add.js status', response.status);
              if (!response.ok) throw new Error('add failed: ' + response.status);
              addSucceeded = true;
              // Small delay so the server has committed the add.
              return new Promise(function (resolve) { setTimeout(resolve, 100); });
            })
            .then(function () {
              // Cache-bust the read so we never render a stale pre-add cart.
              return fetch('/cart.js', { headers: { 'Cache-Control': 'no-cache' }, cache: 'no-store' });
            })
            .then(function (cartResponse) {
              if (DEBUG.on) DEBUG.log('/cart.js status', cartResponse && cartResponse.status);
              if (!cartResponse || !cartResponse.ok) {
                throw new Error('cart fetch failed');
              }
              // DO NOT gate on Content-Type. Shopify serves /cart.js as
              // `text/javascript; charset=utf-8` on many shops (this store
              // included), so the old `indexOf('application/json')` guard threw
              // here — BEFORE refreshDrawerAfterAdd — and the drawer never
              // updated even though the add succeeded. Read the body as text and
              // JSON.parse it defensively; a parse failure is the real error.
              return cartResponse.text().then(function (body) {
                var parsed = parseJSON(body);
                if (!parsed || !Array.isArray(parsed.items)) {
                  throw new Error('cart parse failed');
                }
                return parsed;
              });
            })
            .then(function (cart) {
              button.innerHTML = escapeHtml(strings.added);
              if (DEBUG.on) DEBUG.log('/cart.js item_count', cart && cart.item_count);

              // Coordinated, fault-isolated drawer refresh: dispatches
              // cart:updated, guarantees count + totals, runs the theme's proven
              // renderer, applies a duplicate-safe fallback item render, and
              // keeps the drawer open. A failure in any one step cannot block
              // the others (this was the root-cause bug).
              refreshDrawerAfterAdd(cart);

              // Recompute: the newly-added family is now excluded, so this
              // naturally rolls to the next best recommendation (or hides).
              recompute(cart);
            })
            .catch(function (error) {
              if (window.console && console.error) console.error('[cart-recs] add error:', error);
              if (DEBUG.on) DEBUG.log('ERROR add flow', String(error && error.message || error));
              if (addSucceeded) {
                // The item IS in the cart; only the follow-up refetch failed.
                // Don't claim failure — confirm the add and hide the card.
                button.innerHTML = escapeHtml(strings.added);
                setTimeout(function () { container.style.display = 'none'; }, 1500);
                return;
              }
              button.innerHTML = escapeHtml(strings.error);
              setTimeout(function () {
                button.innerHTML = originalHtml;
                button.disabled = false;
              }, 2000);
            });
        }

        function recompute(cart) {
          var items = cart && Array.isArray(cart.items) ? cart.items : [];
          var result;
          if (DEBUG.on) {
            // Raw item dump BEFORE any processing — after three wrong root-
            // cause theories (Liquid cart|json, /cart?view=compare) each
            // "confirmed" by direct curl yet still not matching what the
            // live widget actually receives, stop inferring the shape and
            // just log it directly, every recompute, no matter its origin.
            try {
              DEBUG.log('RAW items', items.map(function (it) {
                return {
                  keys: it && typeof it === 'object' ? Object.keys(it) : typeof it,
                  handle: it && it.handle,
                  id: it && it.id,
                  variant_id: it && it.variant_id,
                  product_type: it && it.product_type,
                  variant_options: it && it.variant_options,
                  options_with_values: it && it.options_with_values
                };
              }));
            } catch (e) {
              DEBUG.log('ERROR raw item dump', String(e));
            }
          }
          try {
            result = computeRecommendation(items, catalog);
          } catch (e) {
            if (window.console && console.error) console.error('[cart-recs] compute error:', e);
            if (DEBUG.on) DEBUG.log('ERROR compute', String(e));
            hide();
            return;
          }
          if (DEBUG.on) {
            DEBUG.log('recompute — cart item_count', cart && cart.item_count);
            DEBUG.log('chosen recommendation', result && result.product
              ? { handle: result.product.handle, variant: result.variant && result.variant.id, coherent: !!result.coherent }
              : null);
            // Top-5 ranked candidates so a wrong pick is diagnosable from the
            // panel alone — no need to re-derive why a product was skipped.
            try {
              DEBUG.log('top candidates', rankCandidates(items, catalog, 5));
            } catch (e) {
              DEBUG.log('ERROR rankCandidates', String(e));
            }
          }
          render(result);
        }

        // --- initial state ---------------------------------------------------
        function getInitialCart() {
          var el = document.getElementById('initial-cart');
          if (el) {
            var parsed = parseJSON(el.textContent);
            if (parsed) return Promise.resolve(parsed);
          }
          // Content-Type-tolerant read (see /cart.js note in addToCart): Shopify
          // may serve this as text/javascript, so parse the text body ourselves.
          return fetch('/cart.js')
            .then(function (r) { return r.text(); })
            .then(function (body) { return parseJSON(body) || { items: [] }; })
            .catch(function () { return { items: [] }; });
        }

        getInitialCart().then(function (cart) {
          recompute(cart);
        });

        // --- live updates ----------------------------------------------------
        var debounceTimer = null;
        function debouncedRecompute(cart) {
          clearTimeout(debounceTimer);
          debounceTimer = setTimeout(function () {
            recompute(cart);
          }, DEBOUNCE_DELAY);
        }

        document.addEventListener('cart:updated', function (e) {
          var cart = null;
          if (e && e.detail) {
            // Accept both { items: [...] } and { cart: { items: [...] } }.
            cart = Array.isArray(e.detail.items)
              ? e.detail
              : (e.detail.cart && Array.isArray(e.detail.cart.items) ? e.detail.cart : null);
          }
          if (cart) {
            debouncedRecompute(cart);
          } else {
            // Fall back to a fresh fetch if the event carried no usable cart.
            // Content-Type-tolerant read (Shopify may serve text/javascript).
            fetch('/cart.js')
              .then(function (r) { return r.text(); })
              .then(function (body) {
                var parsed = parseJSON(body);
                if (parsed) debouncedRecompute(parsed);
              })
              .catch(function () {});
          }
        });

        if (window.jQuery) {
          window.jQuery(document).on('cart.requestComplete', function (event, cart) {
            if (cart && Array.isArray(cart.items)) {
              debouncedRecompute(cart);
            }
          });
        }
      }

      function boot() {
        var containers = document.querySelectorAll('[data-cart-recs]');
        for (var i = 0; i < containers.length; i++) {
          try {
            setupContainer(containers[i]);
          } catch (e) {
            // A single broken container must never break the page / drawer.
            try { containers[i].style.display = 'none'; } catch (_) {}
            if (window.console && console.error) console.error('[cart-recs] setup error:', e);
          }
        }
      }

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', boot);
      } else {
        boot();
      }
    })();
  }

  return engine;
});
